import FocusBearLogoAnimated from 'assets/icons/FocusBearLogoAnimated';
import LoadingIndicator from 'components/common/LoadingIndicator';
import { POST_HOG_EVENT } from 'constants/events';
import { LOCAL_STORAGE, TIMEOUT_DELAY } from 'constants/general';
import { APP_DEEP_LINKS, ROUTES } from 'constants/routes';
import { useCallback, useEffect, useRef, useState } from 'react';
import { capturePostHogEvent } from 'services/posthog.service';
import { isMobileUA, runAfterDelay } from 'utils/helpers';
import {
  clearDesktopSignInSession,
  clearLocalStorage
} from 'utils/helpers/storage';
import DesktopRedirectFallbackView from './DesktopRedirectFallbackView';
import DesktopRedirectLoading from './DesktopRedirectLoading';
import { useNavigate } from 'react-router-dom';

const DesktopRedirect = () => {
  const navigate = useNavigate();
  const [isFallbackShown, setIsFallbackShown] = useState(false);
  const retryingRef = useRef(false);
  const token = sessionStorage.getItem(LOCAL_STORAGE.AUTH_BEARER_TOKEN_DESKTOP);

  const attemptDeepLink = useCallback((token: string) => {
    capturePostHogEvent(POST_HOG_EVENT.DESKTOP.DEEPLINK_ATTEMPTED, {
      scheme: APP_DEEP_LINKS.LOGIN_FROM_WEB
    });
    window.location.href = `${APP_DEEP_LINKS.LOGIN_FROM_WEB}?access-token=${token}`;
  }, []);

  useEffect(() => {
    // Mobile devices typically can't handle the desktop deep link; show fallback immediately
    if (isMobileUA) {
      setIsFallbackShown(true);
      capturePostHogEvent(POST_HOG_EVENT.DESKTOP.DEEPLINK_FALLBACK_FIRED, {
        reason: 'mobile_ua'
      });
      return;
    }

    if (token) {
      capturePostHogEvent(POST_HOG_EVENT.DESKTOP.REDIRECT_VIEWED);
      const fallbackTimer = runAfterDelay(() => {
        setIsFallbackShown(true);
        capturePostHogEvent(POST_HOG_EVENT.DESKTOP.DEEPLINK_FALLBACK_FIRED);
      }, TIMEOUT_DELAY.AUTH_DESKTOP_REDIRECT);

      const handleVisibility = () => {
        if (document.hidden) {
          capturePostHogEvent(POST_HOG_EVENT.DESKTOP.DEEPLINK_SUCCEEDED);
          clearTimeout(fallbackTimer);
          clearDesktopSignInSession();
          document.removeEventListener('visibilitychange', handleVisibility);
        }
      };
      document.addEventListener('visibilitychange', handleVisibility);
      attemptDeepLink(token);

      return () => {
        document.removeEventListener('visibilitychange', handleVisibility);
        clearTimeout(fallbackTimer);
      };
    }
  }, [attemptDeepLink, token]);

  const handleOnRetry = useCallback(() => {
    capturePostHogEvent(POST_HOG_EVENT.DESKTOP.DEEPLINK_RETRY_CLICKED);
    if (!token) {
      navigate(ROUTES.LOGIN, { replace: true, state: {} });
      return;
    }
    runAfterDelay(() => {
      retryingRef.current = true;
    }, TIMEOUT_DELAY.AUTH_DESKTOP_RETRY_REDIRECT);
    attemptDeepLink(token);
  }, [attemptDeepLink, navigate, token]);

  if (!token) {
    capturePostHogEvent(POST_HOG_EVENT.DESKTOP.AUTH_FLOW_ERROR, {
      reason: 'missing_token'
    });
    clearLocalStorage();
    window.location.pathname = ROUTES.LOGIN;
    return (
      <LoadingIndicator
        dims='~w-6/10 ~min-h-6/10'
        additionalStyles='fixed top-1/2 left-1/2'
      />
    );
  }

  return (
    <section className='w-full min-h-screen flex flex-col items-center justify-center ~gap-6/12 bg-secondary-50 dark:bg-secondary-dark-200 overflow-y-auto scrollbar-thin dark:scrollbar-thumb-secondary-dark-500 dark:scrollbar-track-secondary-dark-300'>
      <FocusBearLogoAnimated />
      {retryingRef.current || !isFallbackShown ? (
        <DesktopRedirectLoading />
      ) : (
        <DesktopRedirectFallbackView onRetry={handleOnRetry} />
      )}
    </section>
  );
};

export default DesktopRedirect;
